import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import useRefsInit from "../hooks/useRefsInit.jsx";
import BryntumContext, { userPreferences } from "../context/bryntumContext.jsx";
import BryntumGridWrapper from "../components/BryntumGrid.jsx";
import { callOnLoadOnColumns } from "../../other/utils/otherUtils.jsx";
import { getOriginalRecord, swivuCheckGridGrants } from "../../other/utils/utils.jsx";
import AuthContext from "../context/authContext.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";
import { useSwivuKanban } from "../hooks/useSwivuKanban";
import { KanbanWrapper } from "@/modern/components/kanban-wrapper";
import { CopyToBoardDialog } from "@/react/components/CopyToBoardDialog";

const TaskPage = () => {
  const gridRef = useRef(null);
  const { setRefs, isRefsInit } = useRefsInit([gridRef]);
  const { user } = useContext(AuthContext);
  const { project, editorHandler, currentViewRef, stateProvider, entityManager, viewManager } = useContext(BryntumContext);
  useEnableViewTracking(gridRef, VIEW_TYPES.tasks, isRefsInit);
  const kanban = useSwivuKanban({ gridRef, storeId: STORE_KEYS.taskStore, entityType: ENTITY_TYPES.TASK });
  const [bulkCopyRecords, setBulkCopyRecords] = useState(null);

  const editorHandlerRef = useRef();
  editorHandlerRef.current = editorHandler;

  useEffect(() => {
    userPreferences.boardPreferencesStore.on(
      "change",
      ({ record }) => {
        if (record?.key === "taskStyle" || record?.key === "taskColor") {
          gridRef.current.instance.refreshRows();
        }
      },
      gridRef.current.instance
    );
  }, []);

  useEffect(() => {
    if (project.extraTaskStore) {
      gridRef.current.instance.store = project.extraTaskStore;
      gridRef.current.instance.store.relayAll(gridRef.current.instance, "swivuTaskStore");
      gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        editorHandler,
        grid: gridRef.current.instance,
        entityName: "Task",
        promptOnRemove: true,
        project,
        tagStoreKey: STORE_KEYS.taskTagValuesStore,
        onBulkCopyTo: (selectedRecords) => setBulkCopyRecords(selectedRecords),
      });
      gridRef.current.instance.onClickSwivuEditMessage = () => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings" });
      };
      swivuCheckGridGrants(gridRef.current.instance, user);
      callOnLoadOnColumns(gridRef.current.instance.columns, project, { editorHandler, stateProvider, currentViewRef, user, entityManager, viewManager });
    }
  }, [project, user, stateProvider, editorHandler, viewManager]);

  const handleKanbanItemDblClick = useCallback(
    (record) => {
      entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: record.id });
    },
    [entityManager]
  );

  return (
    <>
      <ViewSpecificHeader
        viewToggleButtonConfig={{
          isKanbanMode: kanban.isKanbanMode,
          handleOnToggleView: kanban.handleOnToggleView,
          isGroupingApplied: kanban.isGroupingApplied,
          applyGroupingAndOpenKanban: kanban.applyGroupingAndOpenKanban,
          kanbanFieldConfig: kanban.kanbanFieldConfig,
          activeConfig: kanban.activeConfig,
          getActiveGroupOrder: kanban.getActiveGroupOrder,
          openCardFieldsEditor: kanban.openCardFieldsEditor
        }}
      />
      {bulkCopyRecords && (
        <CopyToBoardDialog
          open={!!bulkCopyRecords}
          onOpenChange={(isOpen) => { if (!isOpen) setBulkCopyRecords(null); }}
          records={bulkCopyRecords}
          entityType={ENTITY_TYPES.TASK}
        />
      )}
      <KanbanWrapper
        {...kanban}
        onItemDblClick={handleKanbanItemDblClick}
        grid={gridRef.current?.instance}
        processCellMenuItems={gridRef.current?.instance?.features?.cellMenu?.processItems}
      />
      <BryntumGridWrapper
        stateId
        ref={setRefs(gridRef)}
        flex="1 1 25%"
        onTaskFind={({ point }) => {
          editorHandler.openEditor(
            EDITOR_KEYS.taskFinderEditor,
            null,
            {
              point,
              onTaskClick: ({ record }) => {
                const originalRecord = getOriginalRecord(record);
                entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: originalRecord.id });
              }
            },
            {
              by: "recordType",
              useExisting: true
            }
          );
        }}
        searchFeature={true}
      />
    </>
  );
};

export default TaskPage;
